// Schedule Totals Component - Shows weekly and monthly totals per collaborator
import { useMemo, useState, useEffect } from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, parseISO, getWeek, startOfYear, eachMonthOfInterval, subMonths } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';
import { Clock, CalendarDays, RefreshCw, TrendingUp, Timer, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { MaladieIcon, VacancesIcon, AbsenceIcon } from './AbsenceIcons';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===================== CONSTANTES TYPES D'ABSENCES =====================
// Français par défaut, anglais accepté pour rétrocompatibilité
const CONGE_TYPES = ['conge', 'paidLeave'];
const MALADIE_TYPES = ['maladie', 'sickLeave'];
const SANS_SOLDE_TYPES = ['sans_solde', 'unpaidLeave'];

const isConge = (absenceType) => CONGE_TYPES.includes(absenceType);
const isMaladie = (absenceType) => MALADIE_TYPES.includes(absenceType);
const isSansSolde = (absenceType) => SANS_SOLDE_TYPES.includes(absenceType);

// Calculate hours from time strings
const timeToMinutes = (time) => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export function ScheduleTotals({ 
  schedule, 
  monthSchedule,
  absences, 
  team,
  currentDate,
  periodStartDate, // Date de début de la période affichée - utilisée pour le mois
  language = 'fr',
  // dayHours supprimé - on utilise weekly_contractual_hours de chaque membre
  onRefresh // Callback pour rafraîchir les données
}) {
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  
  // State for overtime hours
  const [overtimeData, setOvertimeData] = useState([]);
  // State for holidays (fetched from API for consistency with backend)
  const [holidayDates, setHolidayDates] = useState(new Set());
  
  // Utiliser periodStartDate comme référence pour TOUT (semaine ET mois affiché)
  const referenceDate = periodStartDate || currentDate;
  const currentYear = referenceDate.getFullYear();
  
  // Fetch holidays from API (Anomalie 7 - garantit cohérence avec backend)
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const res = await axios.get(`${API}/holidays`, { params: { year: currentYear } });
        const nonWorked = Object.entries(res.data || {})
          .filter(([_, h]) => h.isNonWorked)
          .map(([date]) => date);
        setHolidayDates(new Set(nonWorked));
      } catch (error) {
        console.error('Error fetching holidays:', error);
        setHolidayDates(new Set());
      }
    };
    fetchHolidays();
  }, [currentYear]);
  
  // Fetch overtime hours for the current month
  useEffect(() => {
    const fetchOvertime = async () => {
      try {
        const monthStartStr = format(startOfMonth(periodStartDate || currentDate), 'yyyy-MM-dd');
        const monthEndStr = format(endOfMonth(periodStartDate || currentDate), 'yyyy-MM-dd');
        const res = await axios.get(`${API}/overtime?start_date=${monthStartStr}&end_date=${monthEndStr}`);
        setOvertimeData(res.data || []);
      } catch (error) {
        console.error('Error fetching overtime:', error);
        setOvertimeData([]);
      }
    };
    fetchOvertime();
  }, [periodStartDate, currentDate]);
  
  // Calculate date ranges
  // Semaine : basée sur periodStartDate
  const weekStart = startOfWeek(referenceDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(referenceDate, { weekStartsOn: 1 });
  
  // Mois : basé sur periodStartDate (le mois affiché à l'écran)
  const monthStart = startOfMonth(referenceDate);
  const monthEnd = endOfMonth(referenceDate);
  
  // Get week and month date strings
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd }).map(d => format(d, 'yyyy-MM-dd'));
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd }).map(d => format(d, 'yyyy-MM-dd'));
  
  // Get week number and month name from the DISPLAYED period
  const weekNumber = getWeek(weekStart, { weekStartsOn: 1 });
  const monthName = format(monthStart, 'MMMM yyyy', { locale });
  
  // Use holidayDates from API (fetched via useEffect for consistency with backend)
  // Calculate working days (excluding weekends and holidays)
  // If hireDate/departureDate are provided, only count days within the employment period.
  const countWorkingDays = (days, hireDate = null, departureDate = null) => {
    return days.filter(dayStr => {
      if (hireDate && dayStr < hireDate) return false;
      if (departureDate && dayStr > departureDate) return false;
      const date = parseISO(dayStr);
      const dayOfWeek = date.getDay();
      // Exclude weekends (0=Sunday, 6=Saturday) and holidays
      return dayOfWeek !== 0 && dayOfWeek !== 6 && !holidayDates.has(dayStr);
    }).length;
  };
  
  const weekWorkingDays = countWorkingDays(weekDays);
  const monthWorkingDays = countWorkingDays(monthDays);
  
  // Use monthSchedule for month totals if available, otherwise fall back to schedule
  const scheduleForMonth = monthSchedule && monthSchedule.length > 0 ? monthSchedule : schedule;
  
  // Detect users without weekly_contractual_hours (Anomalie 1 - warning)
  const usersWithoutHours = team.filter(m => !m.weekly_contractual_hours);
  
  // Calculate totals per collaborator
  const { weekTotals, monthTotals, weekData, monthData } = useMemo(() => {
    const weekTotalsMap = {};
    const monthTotalsMap = {};
    
    // Initialize for ALL team members (not just those in schedule)
    team.forEach(member => {
      // OBLIGATOIRE: weekly_contractual_hours doit être défini
      // Si absent, on affiche un warning et on utilise 0 (pour forcer la correction)
      const userWeeklyHours = member.weekly_contractual_hours;
      if (!userWeeklyHours) {
        console.warn(`[ScheduleTotals] Heures contractuelles non définies pour ${member.first_name} ${member.last_name}`);
      }
      const userDayHours = userWeeklyHours ? userWeeklyHours / 5 : 0;
      
      // Take into account hire_date/departure_date: expected hours only during employment period
      const memberHireDate = member.hire_date || null;
      const memberDepartureDate = member.departure_date || null;
      const weekExpectedHours = countWorkingDays(weekDays, memberHireDate, memberDepartureDate) * userDayHours;
      const monthExpectedHours = countWorkingDays(monthDays, memberHireDate, memberDepartureDate) * userDayHours;
      
      weekTotalsMap[member.id] = {
        name: `${member.first_name} ${member.last_name}`,
        color: member.color || '#3B82F6',
        hours: 0,
        conges: 0,
        absences: 0,
        overtime: 0,
        expectedHours: weekExpectedHours,
        userDayHours: userDayHours,
        congesEntries: [],
        absencesEntries: []
      };
      monthTotalsMap[member.id] = {
        name: `${member.first_name} ${member.last_name}`,
        color: member.color || '#3B82F6',
        hours: 0,
        conges: 0,
        absences: 0,
        overtime: 0,
        expectedHours: monthExpectedHours,
        userDayHours: userDayHours,
        congesEntries: [],
        absencesEntries: []
      };
    });
    
    // Calculate worked hours from schedule (week only)
    schedule.forEach(slot => {
      if (!weekTotalsMap[slot.user_id]) return;
      
      const startMinutes = timeToMinutes(slot.start_time);
      const endMinutes = timeToMinutes(slot.end_time);
      const netMinutes = (endMinutes - startMinutes) - (slot.break_minutes || 0);
      const hours = Math.max(0, netMinutes / 60);
      
      // Week totals - only for FIRST week displayed
      if (weekDays.includes(slot.date)) {
        weekTotalsMap[slot.user_id].hours += hours;
      }
    });
    
    // Calculate worked hours from monthSchedule (full month)
    scheduleForMonth.forEach(slot => {
      if (!monthTotalsMap[slot.user_id]) return;
      
      const startMinutes = timeToMinutes(slot.start_time);
      const endMinutes = timeToMinutes(slot.end_time);
      const netMinutes = (endMinutes - startMinutes) - (slot.break_minutes || 0);
      const hours = Math.max(0, netMinutes / 60);
      
      // Month totals - for ALL days of the month
      if (monthDays.includes(slot.date)) {
        monthTotalsMap[slot.user_id].hours += hours;
      }
    });
    
    // Calculate absences - using each user's specific daily hours
    // Utilise les constantes CONGE_TYPES, MALADIE_TYPES pour classification (Anomalie 2 & 3)
    (absences || []).forEach(absence => {
      if (!weekTotalsMap[absence.user_id]) return;
      if (absence.status !== 'approved') return;
      
      try {
        const start = parseISO(absence.start_date);
        const end = parseISO(absence.end_date);
        const absenceDays = eachDayOfInterval({ start, end });
        
        // Classification explicite des types d'absences (Anomalie 3)
        const absenceIsConge = isConge(absence.absence_type);
        const absenceIsMaladie = isMaladie(absence.absence_type);
        // sans_solde et autres vont dans 'absences' (maladie visuellement)
        
        // Use the user's specific daily hours (contractual hours / 5)
        const userDayHours = weekTotalsMap[absence.user_id].userDayHours;
        const hoursPerDay = absence.day_part === 'morning' || absence.day_part === 'afternoon' 
          ? userDayHours / 2 
          : userDayHours;
        
        // Track days counted for this absence in week/month
        let weekDaysCount = 0;
        let monthDaysCount = 0;
        
        absenceDays.forEach(day => {
          const dayStr = format(day, 'yyyy-MM-dd');
          const dayOfWeek = day.getDay();
          
          // Skip weekends (0=Sunday, 6=Saturday) and holidays
          if (dayOfWeek === 0 || dayOfWeek === 6 || holidayDates.has(dayStr)) {
            return;
          }
          
          // Week totals
          if (weekDays.includes(dayStr)) {
            weekDaysCount++;
            if (absenceIsConge) {
              weekTotalsMap[absence.user_id].conges += hoursPerDay;
            } else {
              // Maladie, sans_solde, autre → absences
              weekTotalsMap[absence.user_id].absences += hoursPerDay;
            }
          }
          
          // Month totals
          if (monthDays.includes(dayStr)) {
            monthDaysCount++;
            if (absenceIsConge) {
              monthTotalsMap[absence.user_id].conges += hoursPerDay;
            } else {
              monthTotalsMap[absence.user_id].absences += hoursPerDay;
            }
          }
        });
        
        // Store absence entry for tooltip (only if days counted)
        const absenceEntry = {
          start_date: absence.start_date,
          end_date: absence.end_date,
          type: absence.absence_type,
          day_part: absence.day_part,
          reason: absence.reason
        };
        
        if (weekDaysCount > 0) {
          const weekEntry = { ...absenceEntry, hours: weekDaysCount * hoursPerDay, days: weekDaysCount };
          if (absenceIsConge) {
            weekTotalsMap[absence.user_id].congesEntries.push(weekEntry);
          } else {
            weekTotalsMap[absence.user_id].absencesEntries.push(weekEntry);
          }
        }
        
        if (monthDaysCount > 0) {
          const monthEntry = { ...absenceEntry, hours: monthDaysCount * hoursPerDay, days: monthDaysCount };
          if (absenceIsConge) {
            monthTotalsMap[absence.user_id].congesEntries.push(monthEntry);
          } else {
            monthTotalsMap[absence.user_id].absencesEntries.push(monthEntry);
          }
        }
      } catch (e) {
        // Ignore invalid dates
      }
    });
    
    // Add overtime hours to totals and store entries for tooltip
    (overtimeData || []).forEach(entry => {
      if (!entry.user_id) return;
      
      const entryDate = entry.date;
      
      // Week totals - only for entries in the current week
      if (weekDays.includes(entryDate) && weekTotalsMap[entry.user_id]) {
        weekTotalsMap[entry.user_id].overtime += entry.hours || 0;
        if (!weekTotalsMap[entry.user_id].overtimeEntries) {
          weekTotalsMap[entry.user_id].overtimeEntries = [];
        }
        weekTotalsMap[entry.user_id].overtimeEntries.push(entry);
      }
      
      // Month totals - for entries in the current month
      if (monthDays.includes(entryDate) && monthTotalsMap[entry.user_id]) {
        monthTotalsMap[entry.user_id].overtime += entry.hours || 0;
        if (!monthTotalsMap[entry.user_id].overtimeEntries) {
          monthTotalsMap[entry.user_id].overtimeEntries = [];
        }
        monthTotalsMap[entry.user_id].overtimeEntries.push(entry);
      }
    });
    
    // Filter: only show collaborators who have hours, conges, absences or overtime
    const weekDataArr = Object.values(weekTotalsMap).filter(t => t.hours > 0 || t.conges > 0 || t.absences > 0 || t.overtime !== 0);
    const monthDataArr = Object.values(monthTotalsMap).filter(t => t.hours > 0 || t.conges > 0 || t.absences > 0 || t.overtime !== 0);
    
    return { 
      weekTotals: weekTotalsMap, 
      monthTotals: monthTotalsMap,
      weekData: weekDataArr,
      monthData: monthDataArr
    };
  }, [schedule, scheduleForMonth, absences, team, weekDays, monthDays, weekWorkingDays, monthWorkingDays, overtimeData, holidayDates]);

  const labels = {
    fr: {
      weekTotal: `Total Semaine ${weekNumber}`,
      monthTotal: `Total Mois - ${monthName}`,
      collaborator: 'Collaborateur',
      hours: 'Heures',
      leave: 'Congés',
      absence: 'Absence',
      overtime: 'H.Sup',
      total: 'Total',
      expected: 'Attendu',
      gap: 'Écart',
      gapMonth: 'Éc.mois',
      gapYTD: '>01/01',
      noData: 'Aucun créneau'
    },
    en: {
      weekTotal: `Week ${weekNumber} Total`,
      monthTotal: `${monthName} Total`,
      collaborator: 'Collaborator',
      hours: 'Hours',
      leave: 'Leave',
      absence: 'Absence',
      overtime: 'OT',
      total: 'Total',
      expected: 'Expected',
      gap: 'Gap',
      gapMonth: 'Month Gap',
      gapYTD: 'YTD Gap',
      noData: 'No slots'
    },
    de: {
      weekTotal: `Woche ${weekNumber} Summe`,
      monthTotal: `${monthName} Summe`,
      collaborator: 'Mitarbeiter',
      hours: 'Stunden',
      leave: 'Urlaub',
      absence: 'Abwesenheit',
      overtime: 'Überst.',
      total: 'Gesamt',
      expected: 'Soll',
      gap: 'Differenz',
      gapMonth: 'Monat Diff.',
      gapYTD: 'Diff. 01/01',
      noData: 'Keine Einträge'
    }
  };
  
  const t = labels[language] || labels.fr;
  
  // State for YTD (Year-To-Date) cumulative gaps per user
  const [ytdGaps, setYtdGaps] = useState({});
  const [loadingYTD, setLoadingYTD] = useState(false);
  
  // Memoize the month string to use as stable dependency
  const currentMonthStr = format(monthStart, 'yyyy-MM');
  // Note: currentYear est déjà défini plus haut pour le fetch des holidays
  
  // Fetch YTD data (from January 1st to end of previous month)
  useEffect(() => {
    let isCancelled = false;
    
    const fetchYTDData = async () => {
      if (team.length === 0) return;
      
      // Only fetch if we're not in January (no previous months to count)
      const monthIndex = monthStart.getMonth();
      if (monthIndex === 0) {
        setYtdGaps({});
        return;
      }
      
      setLoadingYTD(true);
      try {
        const yearStart = startOfYear(monthStart);
        const prevMonthEnd = endOfMonth(subMonths(monthStart, 1));
        
        // Fetch schedule data from Jan 1 to end of previous month
        const scheduleRes = await axios.get(`${API}/schedule`, {
          params: {
            week_start: format(yearStart, 'yyyy-MM-dd'),
            week_end: format(prevMonthEnd, 'yyyy-MM-dd')
          }
        });
        
        if (isCancelled) return;
        
        // Fetch absences for the same period
        const absencesRes = await axios.get(`${API}/absences`);
        
        if (isCancelled) return;
        
        const ytdAbsences = (absencesRes.data || []).filter(a => {
          if (a.status !== 'approved') return false;
          const startDate = parseISO(a.start_date);
          return startDate >= yearStart && startDate <= prevMonthEnd;
        });
        
        // Fetch overtime for YTD period (Jan 1 to end of previous month)
        const ytdOvertimeRes = await axios.get(`${API}/overtime`, {
          params: {
            start_date: format(yearStart, 'yyyy-MM-dd'),
            end_date: format(prevMonthEnd, 'yyyy-MM-dd')
          }
        });
        
        if (isCancelled) return;
        
        const ytdOvertime = ytdOvertimeRes.data || [];
        
        // Fetch holidays for current year
        const holidaysRes = await axios.get(`${API}/holidays`, { params: { year: currentYear } });
        
        if (isCancelled) return;
        
        const ytdHolidays = new Set(
          Object.entries(holidaysRes.data || {})
            .filter(([_, h]) => h.isNonWorked)
            .map(([date]) => date)
        );
        
        // Calculate YTD days from Jan 1 to end of previous month
        const ytdDays = eachDayOfInterval({ start: yearStart, end: prevMonthEnd }).map(d => format(d, 'yyyy-MM-dd'));
        
        // Calculate working days per month for each user
        const gaps = {};
        team.forEach(member => {
          // OBLIGATOIRE: weekly_contractual_hours (Anomalie 1)
          const userWeeklyHours = member.weekly_contractual_hours;
          if (!userWeeklyHours) {
            console.warn(`[YTD] Heures contractuelles non définies pour ${member.first_name} ${member.last_name}`);
          }
          const userDayHours = userWeeklyHours ? userWeeklyHours / 5 : 0;
          
          // Count working days (exclude weekends and holidays)
          // Skip days outside member's employment period (hire_date / departure_date)
          const memberHireDate = member.hire_date || null;
          const memberDepartureDate = member.departure_date || null;
          const workingDays = ytdDays.filter(dayStr => {
            if (memberHireDate && dayStr < memberHireDate) return false;
            if (memberDepartureDate && dayStr > memberDepartureDate) return false;
            const date = parseISO(dayStr);
            const dow = date.getDay();
            return dow !== 0 && dow !== 6 && !ytdHolidays.has(dayStr);
          }).length;
          
          const expectedHours = workingDays * userDayHours;
          
          // Calculate worked hours (only within employment period)
          let workedHours = 0;
          (scheduleRes.data || []).forEach(slot => {
            if (slot.user_id !== member.id) return;
            if (!ytdDays.includes(slot.date)) return;
            if (memberHireDate && slot.date < memberHireDate) return;
            if (memberDepartureDate && slot.date > memberDepartureDate) return;
            
            const startMin = timeToMinutes(slot.start_time);
            const endMin = timeToMinutes(slot.end_time);
            const netMin = (endMin - startMin) - (slot.break_minutes || 0);
            workedHours += Math.max(0, netMin / 60);
          });
          
          // Calculate absence hours (congés + maladie) - utilise constantes (Anomalie 2 & 3)
          let absenceHours = 0;
          ytdAbsences.forEach(absence => {
            if (absence.user_id !== member.id) return;
            try {
              const start = parseISO(absence.start_date);
              const end = parseISO(absence.end_date);
              const days = eachDayOfInterval({ start, end });
              
              const hoursPerDay = absence.day_part === 'morning' || absence.day_part === 'afternoon' 
                ? userDayHours / 2 : userDayHours;
              
              days.forEach(day => {
                const dayStr = format(day, 'yyyy-MM-dd');
                const dow = day.getDay();
                if (dow === 0 || dow === 6 || ytdHolidays.has(dayStr)) return;
                if (!ytdDays.includes(dayStr)) return;
                // Skip days outside employment period
                if (memberHireDate && dayStr < memberHireDate) return;
                if (memberDepartureDate && dayStr > memberDepartureDate) return;
                absenceHours += hoursPerDay;
              });
            } catch (e) {
              // Skip invalid absence
            }
          });
          
          // Calculate overtime hours for this user in YTD period (only within employment)
          let overtimeHours = 0;
          ytdOvertime.forEach(entry => {
            if (entry.user_id !== member.id) return;
            if (memberHireDate && entry.date && entry.date < memberHireDate) return;
            if (memberDepartureDate && entry.date && entry.date > memberDepartureDate) return;
            overtimeHours += entry.hours || 0;
          });
          
          gaps[member.id] = (workedHours + absenceHours + overtimeHours) - expectedHours;
        });
        
        if (!isCancelled) {
          setYtdGaps(gaps);
        }
      } catch (err) {
        console.error('Error fetching YTD data:', err);
      } finally {
        if (!isCancelled) {
          setLoadingYTD(false);
        }
      }
    };
    
    fetchYTDData();
    
    return () => {
      isCancelled = true;
    };
  }, [currentMonthStr, currentYear, team.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Week totals table - compact version
  const WeekTotalsTable = ({ data, title, icon: Icon }) => {
    const grandTotal = data.reduce((acc, row) => ({
      hours: acc.hours + row.hours,
      conges: acc.conges + row.conges,
      absences: acc.absences + row.absences,
      overtime: acc.overtime + (row.overtime || 0),
      expectedHours: acc.expectedHours + row.expectedHours
    }), { hours: 0, conges: 0, absences: 0, overtime: 0, expectedHours: 0 });
    
    return (
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        <div className="bg-muted/50 px-2 py-1.5 flex items-center justify-between border-b border-border">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            <span className="font-medium text-sm">{title}</span>
          </div>
        </div>
        {data.length === 0 ? (
          <div className="p-3 text-center text-sm text-muted-foreground">
            {t.noData}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-1 font-medium">{t.collaborator}</th>
                  <th className="text-right p-1 font-medium">{t.hours}</th>
                  <th className="text-right p-1 font-medium">
                    <VacancesIcon size={11} className="inline" />
                  </th>
                  <th className="text-right p-1 font-medium">
                    <MaladieIcon size={11} className="inline" />
                  </th>
                  <th className="text-right p-1 font-medium">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span><Timer size={11} className="inline text-purple-600" /></span>
                        </TooltipTrigger>
                        <TooltipContent>{t.overtime}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </th>
                  <th className="text-right p-1 font-medium">{t.total}</th>
                  <th className="text-right p-1 font-medium">{t.expected}</th>
                  <th className="text-right p-1 font-medium">{t.gap}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => {
                  const total = row.hours + row.conges + row.absences + row.overtime;
                  const gap = total - row.expectedHours;
                  return (
                    <tr key={row.userId || row.name || idx} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                      <td className="p-1">
                        <div className="flex items-center gap-1">
                          <div 
                            className="w-2 h-2 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: row.color }}
                          />
                          <span className="truncate max-w-[80px]">{row.name.split(' ')[0]}</span>
                        </div>
                      </td>
                      <td className="text-right p-1 tabular-nums">{row.hours.toFixed(2)}</td>
                      <td className="text-right p-1 tabular-nums">
                        {row.conges > 0 && row.congesEntries?.length > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help text-blue-600">{row.conges.toFixed(2)}</span>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs bg-white dark:bg-slate-800 border shadow-lg">
                              <div className="text-xs space-y-1">
                                <p className="font-semibold border-b border-slate-200 dark:border-slate-600 pb-1 mb-1 text-slate-900 dark:text-white">{t.leave} - Détail</p>
                                {row.congesEntries.map((entry, i) => (
                                  <div key={i} className="flex justify-between gap-4">
                                    <span className="text-slate-600 dark:text-slate-300">
                                      {entry.start_date === entry.end_date ? entry.start_date : `${entry.start_date} → ${entry.end_date}`}
                                      {entry.day_part && entry.day_part !== 'full' && <span className="text-slate-400 ml-1">({entry.day_part === 'morning' ? 'matin' : 'après-midi'})</span>}
                                    </span>
                                    <span className="text-blue-600 dark:text-blue-400 font-medium">{entry.hours.toFixed(1)}h</span>
                                  </div>
                                ))}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-blue-600">{row.conges > 0 ? row.conges.toFixed(2) : '-'}</span>
                        )}
                      </td>
                      <td className="text-right p-1 tabular-nums">
                        {row.absences > 0 && row.absencesEntries?.length > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help text-amber-600">{row.absences.toFixed(2)}</span>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs bg-white dark:bg-slate-800 border shadow-lg">
                              <div className="text-xs space-y-1">
                                <p className="font-semibold border-b border-slate-200 dark:border-slate-600 pb-1 mb-1 text-slate-900 dark:text-white">{t.absence} - Détail</p>
                                {row.absencesEntries.map((entry, i) => (
                                  <div key={i} className="flex justify-between gap-4">
                                    <span className="text-slate-600 dark:text-slate-300">
                                      {entry.start_date === entry.end_date ? entry.start_date : `${entry.start_date} → ${entry.end_date}`}
                                      {entry.day_part && entry.day_part !== 'full' && <span className="text-slate-400 ml-1">({entry.day_part === 'morning' ? 'matin' : 'après-midi'})</span>}
                                    </span>
                                    <span className="text-amber-600 dark:text-amber-400 font-medium">{entry.hours.toFixed(1)}h</span>
                                  </div>
                                ))}
                                {row.absencesEntries.some(e => e.reason) && (
                                  <div className="border-t border-slate-200 dark:border-slate-600 pt-1 mt-1 text-slate-500 dark:text-slate-400 italic text-[11px]">
                                    {row.absencesEntries.filter(e => e.reason).map((e, i) => (
                                      <p key={i} className="truncate">{e.reason}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-amber-600">{row.absences > 0 ? row.absences.toFixed(2) : '-'}</span>
                        )}
                      </td>
                      <td className="text-right p-1 tabular-nums">
                        {row.overtime !== 0 && row.overtimeEntries?.length > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`cursor-help ${row.overtime > 0 ? 'text-purple-600' : 'text-orange-600'}`}>
                                {row.overtime > 0 ? '+' : ''}{row.overtime.toFixed(2)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs bg-white dark:bg-slate-800 border shadow-lg">
                              <div className="text-xs space-y-1">
                                <p className="font-semibold border-b border-slate-200 dark:border-slate-600 pb-1 mb-1 text-slate-900 dark:text-white">{t.overtime} - Détail</p>
                                {row.overtimeEntries.map((entry, i) => (
                                  <div key={i} className="flex justify-between gap-4">
                                    <span className="text-slate-600 dark:text-slate-300">{entry.date}</span>
                                    <span className={entry.hours >= 0 ? 'text-purple-600 dark:text-purple-400 font-medium' : 'text-orange-600 dark:text-orange-400 font-medium'}>
                                      {entry.hours >= 0 ? '+' : ''}{entry.hours}h
                                    </span>
                                  </div>
                                ))}
                                {row.overtimeEntries.some(e => e.reason) && (
                                  <div className="border-t border-slate-200 dark:border-slate-600 pt-1 mt-1 text-slate-500 dark:text-slate-400 italic text-[11px]">
                                    {row.overtimeEntries.filter(e => e.reason).map((e, i) => (
                                      <p key={i} className="truncate">{e.reason}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className={row.overtime > 0 ? 'text-purple-600' : row.overtime < 0 ? 'text-orange-600' : ''}>
                            {row.overtime !== 0 ? (row.overtime > 0 ? '+' : '') + row.overtime.toFixed(2) : '-'}
                          </span>
                        )}
                      </td>
                      <td className="text-right p-1 tabular-nums font-medium">{total.toFixed(2)}</td>
                      <td className="text-right p-1 tabular-nums text-muted-foreground">{row.expectedHours.toFixed(2)}</td>
                      <td className={`text-right p-1 tabular-nums font-medium ${gap >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {gap >= 0 ? '+' : ''}{gap.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-muted/50 font-medium">
                  <td className="p-1">Total</td>
                  <td className="text-right p-1 tabular-nums">{grandTotal.hours.toFixed(2)}</td>
                  <td className="text-right p-1 tabular-nums text-blue-600">{grandTotal.conges > 0 ? grandTotal.conges.toFixed(2) : '-'}</td>
                  <td className="text-right p-1 tabular-nums text-amber-600">{grandTotal.absences > 0 ? grandTotal.absences.toFixed(2) : '-'}</td>
                  <td className={`text-right p-1 tabular-nums ${grandTotal.overtime > 0 ? 'text-purple-600' : grandTotal.overtime < 0 ? 'text-orange-600' : ''}`}>
                    {grandTotal.overtime !== 0 ? (grandTotal.overtime > 0 ? '+' : '') + grandTotal.overtime.toFixed(2) : '-'}
                  </td>
                  <td className="text-right p-1 tabular-nums">{(grandTotal.hours + grandTotal.conges + grandTotal.absences + grandTotal.overtime).toFixed(2)}</td>
                  <td className="text-right p-1 tabular-nums text-muted-foreground">{grandTotal.expectedHours.toFixed(2)}</td>
                  <td className={`text-right p-1 tabular-nums font-medium ${(grandTotal.hours + grandTotal.conges + grandTotal.absences + grandTotal.overtime - grandTotal.expectedHours) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {(grandTotal.hours + grandTotal.conges + grandTotal.absences + grandTotal.overtime - grandTotal.expectedHours) >= 0 ? '+' : ''}
                    {(grandTotal.hours + grandTotal.conges + grandTotal.absences + grandTotal.overtime - grandTotal.expectedHours).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Month totals table with YTD column
  const MonthTotalsTable = ({ data, title, icon: Icon, showRefresh = false }) => {
    const grandTotal = data.reduce((acc, row) => ({
      hours: acc.hours + row.hours,
      conges: acc.conges + row.conges,
      absences: acc.absences + row.absences,
      overtime: acc.overtime + (row.overtime || 0),
      expectedHours: acc.expectedHours + row.expectedHours
    }), { hours: 0, conges: 0, absences: 0, overtime: 0, expectedHours: 0 });
    
    // Calculate grand total YTD gap
    const grandTotalYTD = data.reduce((acc, row) => {
      const monthGap = (row.hours + row.conges + row.absences + (row.overtime || 0)) - row.expectedHours;
      const ytdGap = ytdGaps[row.userId] || 0;
      return acc + ytdGap + monthGap;
    }, 0);
    
    return (
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        <div className="bg-muted/50 px-3 py-2 flex items-center justify-between border-b border-border">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            <span className="font-medium text-sm">{title}</span>
          </div>
          {showRefresh && onRefresh && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onRefresh}
              className="h-7 w-7 p-0"
              title="Rafraîchir"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {data.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {t.noData}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-1 font-medium whitespace-nowrap">{t.collaborator}</th>
                  <th className="text-right p-1 font-medium whitespace-nowrap">{t.hours}</th>
                  <th className="text-right p-1 font-medium whitespace-nowrap">
                    <VacancesIcon size={11} className="inline" />
                  </th>
                  <th className="text-right p-1 font-medium whitespace-nowrap">
                    <MaladieIcon size={11} className="inline" />
                  </th>
                  <th className="text-right p-1 font-medium whitespace-nowrap">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span><Timer size={11} className="inline text-purple-600" /></span>
                        </TooltipTrigger>
                        <TooltipContent>{t.overtime}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </th>
                  <th className="text-right p-1 font-medium whitespace-nowrap">{t.total}</th>
                  <th className="text-right p-1 font-medium whitespace-nowrap">{t.expected}</th>
                  <th className="text-right p-1 font-medium whitespace-nowrap">{t.gapMonth}</th>
                  <th className="text-right p-1 font-medium whitespace-nowrap bg-blue-50 dark:bg-blue-950">
                    <TrendingUp className="h-2.5 w-2.5 inline" />
                    {t.gapYTD}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => {
                  const total = row.hours + row.conges + row.absences + (row.overtime || 0);
                  const monthGap = total - row.expectedHours;
                  const prevYTDGap = ytdGaps[row.userId] || 0;
                  const totalYTDGap = prevYTDGap + monthGap;
                  
                  return (
                    <tr key={idx} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                      <td className="p-1">
                        <div className="flex items-center gap-1">
                          <div 
                            className="w-2 h-2 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: row.color }}
                          />
                          <span className="truncate max-w-[90px]">{row.name.split(' ')[0]}</span>
                        </div>
                      </td>
                      <td className="text-right p-1 tabular-nums">{row.hours.toFixed(2)}</td>
                      <td className="text-right p-1 tabular-nums">
                        {row.conges > 0 && row.congesEntries?.length > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help text-blue-600">{row.conges.toFixed(2)}</span>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs bg-white dark:bg-slate-800 border shadow-lg">
                              <div className="text-xs space-y-1">
                                <p className="font-semibold border-b border-slate-200 dark:border-slate-600 pb-1 mb-1 text-slate-900 dark:text-white">{t.leave} - Détail</p>
                                {row.congesEntries.map((entry, i) => (
                                  <div key={i} className="flex justify-between gap-4">
                                    <span className="text-slate-600 dark:text-slate-300">
                                      {entry.start_date === entry.end_date ? entry.start_date : `${entry.start_date} → ${entry.end_date}`}
                                      {entry.day_part && entry.day_part !== 'full' && <span className="text-slate-400 ml-1">({entry.day_part === 'morning' ? 'matin' : 'après-midi'})</span>}
                                    </span>
                                    <span className="text-blue-600 dark:text-blue-400 font-medium">{entry.hours.toFixed(1)}h</span>
                                  </div>
                                ))}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-blue-600">{row.conges > 0 ? row.conges.toFixed(2) : '-'}</span>
                        )}
                      </td>
                      <td className="text-right p-1 tabular-nums">
                        {row.absences > 0 && row.absencesEntries?.length > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help text-amber-600">{row.absences.toFixed(2)}</span>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs bg-white dark:bg-slate-800 border shadow-lg">
                              <div className="text-xs space-y-1">
                                <p className="font-semibold border-b border-slate-200 dark:border-slate-600 pb-1 mb-1 text-slate-900 dark:text-white">{t.absence} - Détail</p>
                                {row.absencesEntries.map((entry, i) => (
                                  <div key={i} className="flex justify-between gap-4">
                                    <span className="text-slate-600 dark:text-slate-300">
                                      {entry.start_date === entry.end_date ? entry.start_date : `${entry.start_date} → ${entry.end_date}`}
                                      {entry.day_part && entry.day_part !== 'full' && <span className="text-slate-400 ml-1">({entry.day_part === 'morning' ? 'matin' : 'après-midi'})</span>}
                                    </span>
                                    <span className="text-amber-600 dark:text-amber-400 font-medium">{entry.hours.toFixed(1)}h</span>
                                  </div>
                                ))}
                                {row.absencesEntries.some(e => e.reason) && (
                                  <div className="border-t border-slate-200 dark:border-slate-600 pt-1 mt-1 text-slate-500 dark:text-slate-400 italic text-[11px]">
                                    {row.absencesEntries.filter(e => e.reason).map((e, i) => (
                                      <p key={i} className="truncate">{e.reason}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-amber-600">{row.absences > 0 ? row.absences.toFixed(2) : '-'}</span>
                        )}
                      </td>
                      <td className="text-right p-1 tabular-nums">
                        {row.overtime !== 0 && row.overtimeEntries?.length > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`cursor-help ${row.overtime > 0 ? 'text-purple-600' : 'text-orange-600'}`}>
                                {row.overtime > 0 ? '+' : ''}{row.overtime.toFixed(2)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs bg-white dark:bg-slate-800 border shadow-lg">
                              <div className="text-xs space-y-1">
                                <p className="font-semibold border-b border-slate-200 dark:border-slate-600 pb-1 mb-1 text-slate-900 dark:text-white">{t.overtime} - Détail</p>
                                {row.overtimeEntries.map((entry, i) => (
                                  <div key={i} className="flex justify-between gap-4">
                                    <span className="text-slate-600 dark:text-slate-300">{entry.date}</span>
                                    <span className={entry.hours >= 0 ? 'text-purple-600 dark:text-purple-400 font-medium' : 'text-orange-600 dark:text-orange-400 font-medium'}>
                                      {entry.hours >= 0 ? '+' : ''}{entry.hours}h
                                    </span>
                                  </div>
                                ))}
                                {row.overtimeEntries.some(e => e.reason) && (
                                  <div className="border-t border-slate-200 dark:border-slate-600 pt-1 mt-1 text-slate-500 dark:text-slate-400 italic text-[11px]">
                                    {row.overtimeEntries.filter(e => e.reason).map((e, i) => (
                                      <p key={i} className="truncate">{e.reason}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className={row.overtime > 0 ? 'text-purple-600' : row.overtime < 0 ? 'text-orange-600' : ''}>
                            {row.overtime !== 0 ? (row.overtime > 0 ? '+' : '') + row.overtime.toFixed(2) : '-'}
                          </span>
                        )}
                      </td>
                      <td className="text-right p-1 tabular-nums font-medium">{total.toFixed(2)}</td>
                      <td className="text-right p-1 tabular-nums text-muted-foreground">{row.expectedHours.toFixed(2)}</td>
                      <td className={`text-right p-1 tabular-nums font-medium ${monthGap >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {monthGap >= 0 ? '+' : ''}{monthGap.toFixed(2)}
                      </td>
                      <td className={`text-right p-1 tabular-nums font-bold ${totalYTDGap >= 0 ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30' : 'text-red-700 bg-red-50 dark:bg-red-950/30'}`}>
                        {loadingYTD ? '...' : `${totalYTDGap >= 0 ? '+' : ''}${totalYTDGap.toFixed(2)}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-muted/50 font-medium">
                  <td className="p-1">Total</td>
                  <td className="text-right p-1 tabular-nums">{grandTotal.hours.toFixed(2)}</td>
                  <td className="text-right p-1 tabular-nums text-blue-600">{grandTotal.conges > 0 ? grandTotal.conges.toFixed(2) : '-'}</td>
                  <td className="text-right p-1 tabular-nums text-amber-600">{grandTotal.absences > 0 ? grandTotal.absences.toFixed(2) : '-'}</td>
                  <td className={`text-right p-1 tabular-nums ${grandTotal.overtime > 0 ? 'text-purple-600' : grandTotal.overtime < 0 ? 'text-orange-600' : ''}`}>
                    {grandTotal.overtime !== 0 ? (grandTotal.overtime > 0 ? '+' : '') + grandTotal.overtime.toFixed(2) : '-'}
                  </td>
                  <td className="text-right p-1 tabular-nums">{(grandTotal.hours + grandTotal.conges + grandTotal.absences + grandTotal.overtime).toFixed(2)}</td>
                  <td className="text-right p-1 tabular-nums text-muted-foreground">{grandTotal.expectedHours.toFixed(2)}</td>
                  <td className={`text-right p-1 tabular-nums font-medium ${(grandTotal.hours + grandTotal.conges + grandTotal.absences + grandTotal.overtime - grandTotal.expectedHours) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {(grandTotal.hours + grandTotal.conges + grandTotal.absences + grandTotal.overtime - grandTotal.expectedHours) >= 0 ? '+' : ''}
                    {(grandTotal.hours + grandTotal.conges + grandTotal.absences + grandTotal.overtime - grandTotal.expectedHours).toFixed(2)}
                  </td>
                  <td className={`text-right p-1 tabular-nums font-bold ${grandTotalYTD >= 0 ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30' : 'text-red-700 bg-red-50 dark:bg-red-950/30'}`}>
                    {loadingYTD ? '...' : `${grandTotalYTD >= 0 ? '+' : ''}${grandTotalYTD.toFixed(2)}`}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Add userId to month data for YTD lookup
  const monthDataWithUserId = useMemo(() => {
    return Object.entries(monthTotals)
      .filter(([_, t]) => t.hours > 0 || t.conges > 0 || t.absences > 0 || t.overtime !== 0)
      .map(([userId, data]) => ({ ...data, userId }));
  }, [monthTotals]);

  // Warning labels for users without contractual hours
  const warningLabels = {
    fr: 'Heures contractuelles non définies pour :',
    en: 'Contractual hours not defined for:',
    de: 'Vertragsstunden nicht definiert für:'
  };

  return (
    <div className="space-y-4">
      {/* Warning for users without weekly_contractual_hours (Anomalie 1) */}
      {usersWithoutHours.length > 0 && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>
            {warningLabels[language] || warningLabels.fr}{' '}
            <strong>{usersWithoutHours.map(u => `${u.first_name} ${u.last_name}`).join(', ')}</strong>
          </span>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WeekTotalsTable data={weekData} title={t.weekTotal} icon={Clock} />
        <MonthTotalsTable data={monthDataWithUserId} title={t.monthTotal} icon={CalendarDays} showRefresh={true} />
      </div>
    </div>
  );
}
